import React from "react";
import { Helmet } from "react-helmet-async";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import { Container, ContentWithPaddingXl } from "components/misc/Layouts";
import tw from "twin.macro";
import styled from "styled-components";
import { css } from "styled-components/macro"; //eslint-disable-line
import Header from "components/headers/light.js";
import Footer from "components/footers/SimpleFiveColumn.js";
import { SectionHeading } from "components/misc/Headings";

const HeadingRow = tw.div`flex`;
const Heading = tw(SectionHeading)`text-gray-900 mb-10`;
const Text = styled.div`
  ${tw`text-lg  text-gray-800`}
  p {
    ${tw`mt-2 leading-loose`}
  }
  h1 {
    ${tw`text-3xl font-bold mt-10`}
  }
  h2 {
    ${tw`text-2xl font-bold mt-8`}
  }
  h3 {
    ${tw`text-xl font-bold mt-6`}
  }
  ul {
    ${tw`list-disc list-inside`}
    li {
      ${tw`ml-2 mb-3`}
      p {
        ${tw`mt-0 inline leading-normal`}
      }
    }
  }
`;

export default ({ headingText = "Privacy Policy" }) => {
  return (
    <AnimationRevealPage>
      <Helmet>
        <title>Privacy Policy | EthosHub</title>
        <meta name="description" content="How EthosHub collects, stores and protects personal data across our anonymous workplace reporting platform." />
        <link rel="canonical" href="https://www.ethoshub.io/privacy-policy" />
        <meta property="og:title" content="Privacy Policy | EthosHub" />
        <meta property="og:description" content="How EthosHub collects, stores and protects personal data across our anonymous workplace reporting platform." />
        <meta property="og:url" content="https://www.ethoshub.io/privacy-policy" />
      </Helmet>
      <Header />
      <Container>
        <ContentWithPaddingXl>
          <HeadingRow>
            <Heading>{headingText}</Heading>
          </HeadingRow>
          <Text>
            <p>Last updated: 12 June 2025</p>

            <p>
              This Privacy Policy describes the policies and procedures of Inspired Cloud Solutions Ltd (trading as
              EthosHub) on the collection, use, and disclosure of your information when you use the Service. It also
              explains your privacy rights and how the law protects you.
            </p>

            <p>
              We use your personal data to provide and improve the Service. By using the Service, you agree to the
              collection and use of information in accordance with this Privacy Policy.
            </p>

            <h1>Interpretation and Definitions</h1>
            <h2>Interpretation</h2>
            <p>
              Words with an initial capital letter have meanings defined below. These definitions apply whether the
              terms appear in singular or plural form.
            </p>

            <h2>Definitions</h2>
            <p>For the purposes of this Privacy Policy:</p>
            <ul>
              <li>
                <p>
                  <strong>Company</strong> (referred to as "we", "us", or "our") means Inspired Cloud Solutions Ltd,
                  a company registered in England and Wales, trading as EthosHub.
                </p>
              </li>
              <li>
                <p>
                  <strong>You</strong> means the individual accessing or using the Service, or the organisation on
                  behalf of which that individual is acting.
                </p>
              </li>
              <li>
                <strong>Account</strong> means a unique account created for you to access the Service or parts of it.
              </li>
              <li>
                <strong>Website</strong> refers to EthosHub, accessible from{" "}
                <a href="https://www.ethoshub.io">https://www.ethoshub.io</a>.
              </li>
              <li>
                <strong>Service</strong> refers to the Website and the EthosHub platform, including all applications
                and features offered through it.
              </li>
              <li>
                <strong>Country</strong> refers to England and Wales, United Kingdom.
              </li>
              <li>
                <p>
                  <strong>Service Provider</strong> means any natural or legal person who processes data on behalf of
                  the Company — for example, hosting providers, email delivery services, or payment processors.
                </p>
              </li>
              <li>
                <p>
                  <strong>Personal Data</strong> means any information relating to an identified or identifiable
                  individual.
                </p>
              </li>
              <li>
                <strong>Special Category Data</strong> means personal data that is particularly sensitive under UK
                GDPR, including information revealing racial or ethnic origin, health, religious beliefs, or data
                relating to criminal convictions — categories which may arise in workplace misconduct reports submitted
                through the Service.
              </li>
              <li>
                <strong>Cookies</strong> are small files placed on your device by a website that record browsing
                activity and preferences.
              </li>
              <li>
                <strong>Usage Data</strong> refers to data collected automatically from your use of the Service, such
                as pages visited, time spent, and device information.
              </li>
            </ul>

            <h1>Collecting and Using Your Personal Data</h1>
            <h2>Types of Data Collected</h2>

            <h3>Personal Data</h3>
            <p>
              When you use the Service, we may ask you to provide personally identifiable information, including but
              not limited to:
            </p>
            <ul>
              <li>Name and email address</li>
              <li>Organisation name and job role</li>
              <li>Content you include in incident reports and messages</li>
              <li>Payment and billing information (processed by Stripe — we do not store card details directly)</li>
              <li>Usage Data</li>
            </ul>

            <h3>Special Category Data in Incident Reports</h3>
            <p>
              The nature of EthosHub's Service means that incident reports submitted by employees may contain Special
              Category Data — for example, reports involving racial discrimination, harassment, or health-related
              matters. We process this data solely to provide the case management service contracted by the
              subscribing organisation. The legal basis for processing Special Category Data is Article 9(2)(b) of UK
              GDPR (processing necessary for carrying out obligations in the field of employment and social security
              law) and, where applicable, Article 9(2)(g) (substantial public interest).
            </p>

            <h3>Usage Data</h3>
            <p>Usage Data is collected automatically when you use the Service.</p>
            <p>
              This may include your device's IP address, browser type and version, pages visited, time and date of
              visits, time spent on pages, and other diagnostic data. When you access the Service via a mobile device,
              we may also collect device type, unique device identifiers, and mobile operating system information.
            </p>
            <p>
              Anonymous reporters are a special case: we take deliberate steps not to log or retain IP addresses or
              any other identifying technical data for anonymous submissions. Only a hashed token (never the raw
              token) is stored to allow anonymous follow-up access.
            </p>

            <h3>Cookies</h3>
            <p>
              We use Cookies and similar tracking technologies to maintain session state and remember your
              preferences.
            </p>
            <ul>
              <li>
                <p><strong>Necessary / Essential Cookies</strong></p>
                <p>Type: Session Cookies — Administered by: Us</p>
                <p>
                  Required to authenticate users and maintain secure sessions. Without these, the Service cannot
                  function.
                </p>
              </li>
              <li>
                <p><strong>Functionality Cookies</strong></p>
                <p>Type: Persistent Cookies — Administered by: Us</p>
                <p>
                  Remember your login details and preferences so you do not need to re-enter them on each visit.
                </p>
              </li>
            </ul>
            <p>
              You can instruct your browser to refuse cookies, though some parts of the Service may not function
              correctly without them.
            </p>

            <h2>Legal Bases for Processing Your Personal Data</h2>
            <p>
              Under UK GDPR, we rely on the following legal bases to process personal data:
            </p>
            <ul>
              <li>
                <strong>Contract performance:</strong> Processing necessary to provide the Service you or your
                organisation have subscribed to, including account management and case handling.
              </li>
              <li>
                <strong>Legitimate interests:</strong> Improving and securing the Service, preventing fraud, and
                communicating service updates — where our interests are not overridden by your rights.
              </li>
              <li>
                <strong>Legal obligation:</strong> Retaining certain data to comply with legal or regulatory
                requirements.
              </li>
              <li>
                <strong>Consent:</strong> Where you have opted in to marketing communications. You may withdraw
                consent at any time.
              </li>
            </ul>

            <h2>How We Use Your Personal Data</h2>
            <p>We use personal data for the following purposes:</p>
            <ul>
              <li>
                <strong>To provide and maintain the Service</strong>, including account creation, case management, and
                messaging features.
              </li>
              <li>
                <strong>To manage your account</strong> and provide access to the relevant features for your role.
              </li>
              <li>
                <strong>To process payments</strong> and manage subscription billing through Stripe.
              </li>
              <li>
                <strong>To send service communications</strong> — including account setup details, case status
                updates, and security notices.
              </li>
              <li>
                <strong>To manage your requests</strong> and respond to support enquiries.
              </li>
              <li>
                <strong>To improve the Service</strong> through usage analysis and performance monitoring.
              </li>
            </ul>

            <h2>Sharing Your Personal Data</h2>
            <p>We may share personal data with the following categories of recipient:</p>
            <ul>
              <li>
                <strong>Service Providers (Data Processors):</strong> Third-party providers who process data on our
                behalf under strict data processing agreements. Our current key processors include:
                <ul>
                  <li>Amazon Web Services (AWS) — cloud hosting and file storage (eu-west-2 region, UK)</li>
                  <li>Neon — database hosting</li>
                  <li>Stripe — payment processing</li>
                  <li>Resend / Amazon SES — transactional email delivery</li>
                </ul>
              </li>
              <li>
                <strong>Subscribing organisations:</strong> Case data is accessible to authorised caseworkers and
                administrators within the organisation that holds the EthosHub subscription. We act as a data
                processor on behalf of that organisation (the data controller) in relation to employee report data.
              </li>
              <li>
                <strong>Legal and regulatory authorities:</strong> Where required by law, court order, or valid
                request from a public authority.
              </li>
              <li>
                <strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of assets, your
                data may be transferred to the successor entity, with prior notice provided.
              </li>
            </ul>

            <h2>Retention of Your Personal Data</h2>
            <p>
              We retain personal data only for as long as necessary to fulfil the purposes described in this Policy,
              or as required by law. Specifically:
            </p>
            <ul>
              <li>Account data is retained for the duration of the subscription and a reasonable period thereafter.</li>
              <li>Case and incident report data is retained in accordance with the subscribing organisation's data
              retention settings and applicable employment law obligations.</li>
              <li>Anonymous session tokens are stored in hashed form only and expire after 90 days by default.</li>
              <li>Payment records are retained as required by HMRC and financial regulations.</li>
            </ul>

            <h2>International Data Transfers</h2>
            <p>
              Our primary infrastructure is hosted within the UK (AWS eu-west-2). Some of our Service Providers (such
              as Stripe) may process data in the United States or other countries. Where data is transferred outside
              the UK, we ensure appropriate safeguards are in place — such as Standard Contractual Clauses or
              adequacy decisions — in accordance with UK GDPR Chapter V.
            </p>

            <h2>Security of Your Personal Data</h2>
            <p>
              We implement appropriate technical and organisational measures to protect your personal data, including
              encryption at rest and in transit, access controls, and role-based permissions. However, no method of
              internet transmission or electronic storage is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h1>Your Rights Under UK GDPR</h1>
            <p>You have the following rights in relation to your personal data:</p>
            <ul>
              <li><strong>Right of access</strong> — request a copy of the personal data we hold about you.</li>
              <li><strong>Right to rectification</strong> — request correction of inaccurate or incomplete data.</li>
              <li><strong>Right to erasure</strong> — request deletion of your data where there is no legitimate
              reason to continue processing it.</li>
              <li><strong>Right to restrict processing</strong> — request that we limit how we use your data in
              certain circumstances.</li>
              <li><strong>Right to data portability</strong> — receive your data in a structured, machine-readable
              format.</li>
              <li><strong>Right to object</strong> — object to processing based on legitimate interests or for direct
              marketing.</li>
              <li><strong>Rights related to automated decision-making</strong> — EthosHub does not make solely
              automated decisions with legal or significant effects on individuals.</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at{" "}
              <a href="mailto:privacy@ethoshub.io">privacy@ethoshub.io</a>. We will respond within one month.
            </p>
            <p>
              You also have the right to lodge a complaint with the UK Information Commissioner's Office (ICO) at{" "}
              <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer">ico.org.uk</a> or by calling
              0303 123 1113.
            </p>

            <h1>Children's Privacy</h1>
            <p>
              The Service is intended for use by organisations and their employees. It is not directed at anyone under
              the age of 16. We do not knowingly collect personal data from children. If you believe a child has
              provided us with personal data, please contact us and we will take steps to remove it.
            </p>

            <h1>Links to Other Websites</h1>
            <p>
              Our Service may contain links to third-party websites. We are not responsible for the privacy practices
              or content of those sites and encourage you to review their privacy policies.
            </p>

            <h1>Changes to this Privacy Policy</h1>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of material changes by email
              or via a prominent notice on the Service, and will update the "Last updated" date at the top of this
              page. Continued use of the Service after changes take effect constitutes acceptance of the updated
              Policy.
            </p>

            <h1>Contact Us</h1>
            <p>If you have any questions about this Privacy Policy or wish to exercise your data rights, please contact:</p>
            <p>
              <strong>Inspired Cloud Solutions Ltd</strong> (trading as EthosHub)<br />
              Email: <a href="mailto:privacy@ethoshub.io">privacy@ethoshub.io</a><br />
              Website: <a href="https://www.ethoshub.io">www.ethoshub.io</a>
            </p>
          </Text>
        </ContentWithPaddingXl>
      </Container>
      <Footer />
    </AnimationRevealPage>
  );
};
